/**
 * Localized, actionable card for a classified LLM-provider chat error.
 *
 * The chat WS frame from frozen openclaw core carries only a raw upstream error
 * string; `useChatSession` classifies it CLIENT-SIDE via `@lexiun/lx-llm-errors`
 * into a `ChatErrorMessage` envelope (kind + affordance + tone + i18nKey), and
 * this card renders the localized copy + the right action. It NEVER shows the
 * raw vendor string (that only goes to the scrubbed operational log). Mirrors
 * the `ChatLLMKeysGate` visual conventions. See
 * ai-docs/lexruntime-error-handling-plan.md Pillar B.
 */

import { AlertTriangle, Info, KeyRound, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ChatErrorMessage } from "@lexiun/lx-llm-errors";

interface ChatLLMProviderErrorCardProps {
  error: ChatErrorMessage;
  /** Re-send the last user prompt (same stopgap retry as the flat-string card). */
  onRetry?: () => void;
  isStreaming?: boolean;
}

const TONE = {
  auth: {
    wrap: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200",
    icon: KeyRound,
    iconClass: "text-amber-600 dark:text-amber-400",
    body: "text-amber-800 dark:text-amber-300",
    action: "bg-amber-600 hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600",
  },
  transport: {
    wrap: "border-red-200 bg-red-50 text-red-900 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200",
    icon: AlertTriangle,
    iconClass: "text-red-600 dark:text-red-400",
    body: "text-red-800 dark:text-red-300",
    action: "bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600",
  },
  info: {
    wrap: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-200",
    icon: Info,
    iconClass: "text-blue-600 dark:text-blue-400",
    body: "text-blue-800 dark:text-blue-300",
    action: "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600",
  },
} as const;

export function ChatLLMProviderErrorCard({
  error,
  onRetry,
  isStreaming,
}: ChatLLMProviderErrorCardProps) {
  const { t } = useTranslation();
  const tone = TONE[error.tone] ?? TONE.transport;
  const Icon = tone.icon;
  const provider = error.providerLabel ?? t("llmErrors.llm.genericProvider");
  const title = t(`${error.i18nKey}.title`, { provider });
  const body = t(`${error.i18nKey}.body`, {
    provider,
    seconds: error.retryAfterSeconds ?? 0,
  });

  const showRetry =
    Boolean(onRetry) &&
    !isStreaming &&
    (error.affordance === "manual-retry" || error.affordance === "auto-retry");
  const showAdmin = error.affordance === "contact-admin";

  return (
    <div
      data-testid="chat-llm-error-card"
      data-kind={error.kind}
      className={`m-1 flex flex-col gap-2 rounded-lg border p-3 text-body-sm ${tone.wrap}`}
    >
      <div className="flex items-start gap-2.5">
        <Icon size={18} className={`mt-0.5 shrink-0 ${tone.iconClass}`} aria-hidden="true" />
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="font-semibold">{title}</h3>
          <p className={`text-xs ${tone.body}`}>{body}</p>
          {error.kind === "unknown" && error.traceId && (
            <p className={`text-[11px] opacity-70 ${tone.body}`}>
              {t("llmErrors.llm.traceId", { id: error.traceId })}
            </p>
          )}
        </div>
      </div>
      {(showRetry || showAdmin) && (
        <div className="flex items-center gap-2 pl-7">
          {showRetry && (
            <button
              type="button"
              data-testid="chat-llm-error-retry"
              onClick={onRetry}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white transition-colors ${tone.action}`}
            >
              <RotateCcw size={13} />
              {t("llmErrors.llm.retry")}
            </button>
          )}
          {showAdmin && (
            <Link
              to="/org/config"
              data-testid="chat-llm-error-admin-link"
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white transition-colors ${tone.action}`}
            >
              {t("llmErrors.llm.reconnect")}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
